import { FormData, AnalysisData, AudiencePersona, FeedbackItem, Improvement, Attachment } from '@/types';
import { GoogleGenerativeAI } from '@google/generative-ai';
import logger from '@/utils/logger';

/**
 * Gemini API client configuration
 */
// Get API key from environment variables ONLY
const GEMINI_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
logger.debug('API Key available:', !!GEMINI_API_KEY); // Don't log the actual key for security
logger.debug('API Key first few chars:', GEMINI_API_KEY ? GEMINI_API_KEY.substring(0, 8) + '...' : 'not available');
logger.debug('API Key length:', GEMINI_API_KEY ? GEMINI_API_KEY.length : 0);

// Remove the check for 'your_actual_gemini_api_key_here' as it should no longer exist in the code
const isValidApiKey = !!GEMINI_API_KEY && GEMINI_API_KEY.length > 10;
logger.debug('API Key validation:', {
  exists: !!GEMINI_API_KEY,
  hasMinLength: GEMINI_API_KEY && GEMINI_API_KEY.length > 10,
  isValid: isValidApiKey
});

// Initialize the Google Generative AI client
const genAI = new GoogleGenerativeAI(GEMINI_API_KEY || '');
// We'll try multiple models in order of preference
const MODEL_NAMES = [
  'gemini-2.5-flash-preview-04-17',
  'gemini-2.5-pro-preview-05-06', 
  'gemini-1.5-pro', 
  'gemini-1.0-pro',
  'gemini-pro'
];

/**
 * Helper function to convert a File to base64
 * Handles error cases and size validation according to Gemini API requirements
 * Returns an object with the base64 data and mimeType
 */
async function fileToBase64(file: File): Promise<{data: string, mimeType: string} | null> {
  return new Promise((resolve, reject) => {
    // Calculate file size in MB
    const fileSizeMB = file.size / (1024 * 1024);
    
    // Log file details for debugging
    console.log(`[Media Processing] Processing file: ${file.name} (${fileSizeMB.toFixed(2)}MB, ${file.type})`);
    logger.info(`Processing file: ${file.name} (${fileSizeMB.toFixed(2)}MB, ${file.type})`);
    
    // Check if file is too large for Gemini API (20MB limit for total request)
    // Since the base64 encoding increases size by ~33%, limit to ~15MB
    if (fileSizeMB > 15) {
      console.error(`[Media Processing] File too large: ${file.name} (${fileSizeMB.toFixed(2)}MB > 15MB limit)`);
      logger.error(`File too large for inline processing: ${fileSizeMB.toFixed(2)}MB (limit: 15MB)`);
      reject(new Error('File too large for inline processing in API request. Maximum size is 15MB.'));
      return;
    }
    
    // Safety check for empty files
    if (file.size === 0) {
      console.error(`[Media Processing] Empty file: ${file.name}`);
      logger.error('Cannot process empty file');
      reject(new Error('File is empty'));
      return;
    }

    // Validate MIME type for videos
    if (file.type.startsWith('video/')) {
      const supportedVideoTypes = [
        'video/mp4', 'video/mpeg', 'video/mov', 'video/avi', 
        'video/x-flv', 'video/mpg', 'video/webm', 'video/wmv', 'video/3gpp'
      ];
      
      console.log(`[Media Processing] Video type: ${file.type} (supported: ${supportedVideoTypes.includes(file.type)})`);
      
      if (!supportedVideoTypes.includes(file.type)) {
        console.warn(`[Media Processing] Potentially unsupported video format: ${file.type}`);
        logger.warn(`Potentially unsupported video format: ${file.type}. Supported formats are: ${supportedVideoTypes.join(', ')}`);
      }
    }

    console.log(`[Media Processing] Starting file read for: ${file.name}`);
    const reader = new FileReader();
    
    // Set a timeout to prevent hanging on large files
    const timeout = setTimeout(() => {
      reader.abort();
      console.error(`[Media Processing] Timeout reading file: ${file.name}`);
      logger.error(`File read timeout for ${file.name}`);
      reject(new Error('File read timeout - processing took too long'));
    }, 30000); // 30 second timeout
    
    reader.readAsDataURL(file);
    
    reader.onload = () => {
      clearTimeout(timeout);
      console.log(`[Media Processing] File read complete: ${file.name}`);
      
      if (typeof reader.result === 'string') {
        // Remove the data URI prefix (e.g., "data:image/jpeg;base64,")
        const parts = reader.result.split(',');
        if (parts.length !== 2) {
          console.error(`[Media Processing] Invalid data URL format: ${file.name}`);
          logger.error(`Invalid data URL format for file: ${file.name}`);
          resolve(null);
          return;
        }
        
        const base64 = parts[1];
        const base64SizeKB = Math.round(base64.length / 1024);
        console.log(`[Media Processing] Successfully encoded file: ${file.name} (base64 size: ${base64SizeKB}KB)`);
        logger.info(`Successfully encoded file: ${file.name} (base64 size: ${base64SizeKB}KB)`);
        
        resolve({
          data: base64,
          mimeType: file.type
        });
      } else {
        console.error(`[Media Processing] Unable to convert to base64: ${file.name}, result type: ${typeof reader.result}`);
        logger.error(`Unable to convert file to base64: ${file.name}, result type: ${typeof reader.result}`);
        resolve(null);
      }
    };
    
    reader.onerror = (error) => {
      clearTimeout(timeout);
      console.error(`[Media Processing] Error reading file ${file.name}:`, error);
      logger.error(`Error reading file ${file.name}:`, error);
      reject(error);
    };
    
    reader.onabort = () => {
      clearTimeout(timeout);
      console.warn(`[Media Processing] File read aborted: ${file.name}`);
      logger.warn(`File read aborted for ${file.name}`);
      reject(new Error('File read aborted'));
    };
  });
}

/**
 * Makes a request to the Gemini API using the Google Generative AI SDK
 */
async function callGeminiAPI(prompt: string, operationType: string): Promise<any> {
  // Log the operation type for debugging
  logger.info(`Gemini API call started: ${operationType}`);
  
  // Skip API call if no valid key is available
  if (!isValidApiKey) {
    logger.warn(`No valid API key found for ${operationType} - key exists:`, !!GEMINI_API_KEY);
    throw new Error('API key is invalid or missing. Please check your API key configuration.');
  }
  
  let lastError = null;
  
  // Check if the prompt contains pre-processed media content before API call
  // This will be marked with strings like "VIDEO CONTENT DESCRIPTION:" or "IMAGE CONTENT DESCRIPTION:"
  const hasPreProcessedMedia = prompt.includes('VIDEO CONTENT DESCRIPTION:') || 
                              prompt.includes('IMAGE CONTENT DESCRIPTION:');
  
  if (hasPreProcessedMedia) {
    logger.info(`Using pre-processed media content for ${operationType}`);
    console.log(`[Gemini API] Using pre-processed media content for ${operationType}`);
    // No need to extract media data since it's already processed as text
    prompt = prompt.replace(/\[(?:VIDEO|IMAGE)_DATA:[^\]]+\]/g, '');
  }
  
  // Try each model in order until one works
  for (const modelName of MODEL_NAMES) {
    try {
      logger.info(`Trying Gemini model for ${operationType}: ${modelName}...`);
      const model = genAI.getGenerativeModel({ model: modelName });
      
      // Check if prompt includes video or image attachment references that weren't pre-processed
      const mediaDataPattern = /\[(VIDEO|IMAGE)_DATA:([^\]]+)\]/g;
      const promptContainsMediaData = !hasPreProcessedMedia && mediaDataPattern.test(prompt);
      
      let result;
      
      if (promptContainsMediaData) {
        console.log(`[Gemini API] Media detected in ${operationType} request`);
        logger.info(`Prompt contains inline media data for ${operationType}`);
        
        // Extract text and media parts for the contents array
        const contentParts = [];
        let textBeforeMedia = '';
        let textAfterMedia = '';
        let lastIndex = 0;
        let match;
        
        // Reset regex state
        mediaDataPattern.lastIndex = 0;
        
        // Find first media marker
        match = mediaDataPattern.exec(prompt);
        if (match) {
          // Get text before the first media marker
          if (match.index > 0) {
            textBeforeMedia = prompt.substring(0, match.index).trim();
          }
          
          // Get media data
          const mediaType = match[1].toLowerCase();
          const base64Data = match[2];
          
          // Log media info but not the actual data
          const dataSizeKB = Math.round(base64Data.length / 1024);
          console.log(`[Gemini API] Including ${mediaType} data in request (${dataSizeKB}KB)`);
          logger.info(`Including ${mediaType} data in request (${dataSizeKB}KB)`);
          
          // Get MIME type based on media type
          let mimeType = mediaType === 'video' ? 'video/mp4' : 'image/jpeg';
          console.log(`[Gemini API] Using MIME type: ${mimeType} for ${mediaType} content`);
          
          // Create media part
          const mediaPart = {
            inlineData: {
              mimeType,
              data: base64Data
            }
          };
          
          // Get text after the media marker
          textAfterMedia = prompt.substring(match.index + match[0].length).trim();
          
          // Properly format request according to Gemini docs (media first, then text)
          // Adding text as separate parts to improve response quality
          const contents = [
            {
              role: 'user',
              parts: [
                mediaPart,
                { text: textBeforeMedia + '\n\n' + textAfterMedia }
              ]
            }
          ];
          
          // Set generation config with higher temperature for video content
          const generationConfig = {
            temperature: modelName.includes('2.5') ? 0.7 : 0.6,
            topP: 0.8,
            topK: 40,
            maxOutputTokens: 1024,
          };
          
          console.log(`[Gemini API] Sending request with ${mediaType} data to model: ${modelName}`);
          logger.info(`Sending request with inline ${mediaType} data to Gemini API`);
          
          try {
            result = await model.generateContent({
              contents,
              generationConfig
            });
            console.log(`[Gemini API] Successfully received response with ${mediaType} data`);
          } catch (error) {
            console.error(`[Gemini API] Error processing ${mediaType} content:`, error);
            throw error;
          }
        } else {
          // This shouldn't happen, but just in case, fall back to standard text processing
          console.error(`[Gemini API] Media pattern test indicated content, but no match was found`);
          logger.error('Media pattern test indicated media content, but no match was found. Falling back to text-only prompt.');
          throw new Error('Failed to extract media data from prompt. Please try again or contact support.');
        }
      } else {
        // Standard text-only prompt or pre-processed media content (treated as text)
        console.log(`[Gemini API] ${hasPreProcessedMedia ? 'Pre-processed media' : 'Text-only'} request for ${operationType} to model: ${modelName}`);
        result = await model.generateContent(prompt);
      }
      
      // Result should be defined at this point, but add a check just in case
      if (!result) {
        logger.error(`No result generated for ${operationType} with model ${modelName}`);
        throw new Error('No result generated from API call');
      }
      
      const response = result.response;
      const text = response.text();
      
      logger.info(`Gemini API response received successfully for ${operationType} from model:`, modelName);
      return text;
    } catch (error: any) {
      logger.error(`Error with model ${modelName} during ${operationType}:`, error);
      lastError = error;
      
      // Check if this is a quota or rate limit error
      const errorMessage = error?.toString?.()?.toLowerCase() || '';
      if (errorMessage.includes('quota') || errorMessage.includes('rate limit') || errorMessage.includes('resource exhausted')) {
        logger.warn('API quota or rate limit reached. Stopping model attempts to avoid further quota usage.');
        throw new Error('API quota or rate limit reached. Please try again later or check your API key limits.');
      }
    }
  }
  
  // If we get here, all models failed
  logger.error(`All Gemini models failed for ${operationType}. Last error:`, lastError);
  throw new Error(`Failed to generate response from Gemini API for ${operationType}. Please check your API key and network connection.`);
}

/**
 * Determines the likely gender based on a first name
 * This is a very simplified approach for avatar generation purposes only
 */
function determineGender(name: string): 'male' | 'female' {
  const firstName = name.split(' ')[0].toLowerCase();
  
  // Comprehensive list of female names from various cultures and backgrounds
  const femaleNames = new Set([
    // Western/English female names
    'sarah', 'emily', 'jessica', 'jennifer', 'emma', 'olivia', 'ava', 'sophia', 'lisa', 'rachel', 'amanda', 
    'michelle', 'linda', 'elizabeth', 'mary', 'patricia', 'ashley', 'maria', 'nicole', 'susan', 'margaret', 
    'barbara', 'alice', 'evelyn', 'sofia', 'anna', 'laura', 'katherine', 'catherine', 'karen', 'diane', 'ruth', 
    'joyce', 'helen', 'deborah', 'sandra', 'kimberly', 'donna', 'carol', 'sharon', 'cynthia', 'rebecca', 
    'samantha', 'grace', 'jane', 'melissa', 'danielle', 'lauren', 'victoria', 'natalie', 'hannah', 'julia', 
    'angela', 'stephanie', 'caroline', 'amelia', 'claire', 'abigail', 'megan', 'alexandra', 'kathryn', 
    'lillian', 'eleanor', 'zoe', 'leah', 'haley', 'gabriella', 'eliza', 'rose', 'madison', 'taylor', 'diana', 
    'ella', 'isabelle', 'isabella', 'aria', 'charlotte', 'allison', 'savannah', 'audrey', 'camila', 'scarlett', 
    'stella', 'maya', 'lila', 'violet', 'lily', 'nova', 'willow', 'ivy', 'eve', 'lucy', 'isabel', 'jasmine', 
    'ariana', 'arianna', 'gabrielle', 'nora', 'valentina', 'reagan', 'hailey', 'piper', 'peyton', 'penelope', 
    'alexa', 'mariah', 'sierra', 'katelyn', 'daisy', 'kylie', 'molly', 'trinity', 'faith', 'joy', 'melody', 
    'harmony', 'hope', 'destiny', 'genesis', 'jordyn', 'jacqueline', 'janet', 'joan', 'juanita', 'judith', 
    'judy', 'juliana', 'julie', 'kara', 'kathleen', 'kathy', 'latoya', 'sylvia', 'tiffany', 'amy', 'nancy', 
    'betty', 'dorothy', 'lori', 'brenda', 'clara', 'kelly', 'kim', 'tracy', 'april', 'june', 'may', 'autumn', 
    'summer', 'winter', 'spring', 'heather', 'holly', 'crystal', 'dawn', 'ana', 'ann', 'anne', 'annie', 'amber', 
    'brittany', 'cheryl', 'christina', 'christine', 'courtney', 'crystal', 'caitlin', 'caitlyn', 'denise',

    // Hispanic/Latina names
    'ana', 'alejandra', 'beatriz', 'camila', 'carla', 'carmen', 'catalina', 'claudia', 'dolores', 'elena', 
    'esperanza', 'fernanda', 'gabriela', 'graciela', 'guadalupe', 'ines', 'juana', 'leticia', 'lucia', 'luisa', 
    'marisol', 'mercedes', 'miriam', 'natalia', 'paloma', 'pilar', 'rosa', 'rosario', 'soledad', 'teresa', 
    'valentina', 'veronica', 'ximena', 'yolanda', 'teresa',
    
    // European names
    'agata', 'agnieszka', 'alessandra', 'amelie', 'anastasia', 'angelica', 'anita', 'anja', 'annette', 'astrid', 
    'bianca', 'brigitte', 'camille', 'celine', 'chiara', 'dagmar', 'elisa', 'elise', 'elodie', 'emilia', 
    'fabienne', 'federica', 'francesca', 'franziska', 'freya', 'frieda', 'gisela', 'greta', 'hanna', 'hedwig', 
    'helga', 'henriette', 'ingrid', 'irene', 'iris', 'joana', 'johanna', 'josefine', 'karoline', 'katja', 
    'klara', 'lena', 'lina', 'luisa', 'magdalena', 'manuela', 'margot', 'marguerite', 'marina', 'marlene', 
    'mathilde', 'nadine', 'olga', 'renate', 'sabine', 'simone', 'sofia', 'sonia', 'svenja', 'tatiana', 
    'ursula', 'vanessa', 'viktoria', 'yvonne', 'zoe',
    
    // Asian names
    'ai', 'akiko', 'ayu', 'chen', 'chun', 'eun', 'fang', 'haruka', 'hina', 'hiroko', 'huan', 'jing', 'jin', 
    'jiaying', 'jiyeon', 'jun', 'kaori', 'keiko', 'kumiko', 'lan', 'li', 'lihua', 'ling', 'mei', 'mika', 
    'miki', 'ming', 'minji', 'misaki', 'na', 'naomi', 'phuong', 'qing', 'rin', 'sakura', 'seoyun', 'shu', 
    'sook', 'subin', 'sujin', 'sumiko', 'sun', 'tomoko', 'wen', 'xia', 'xiuying', 'yan', 'yeo', 'yoon', 
    'yoshiko', 'yu', 'yue', 'yuki', 'yun', 'zheng', 'zhihui', 'zixin',
    
    // Middle Eastern names
    'aida', 'amina', 'amira', 'dalal', 'dalia', 'dana', 'farida', 'fatima', 'habiba', 'hala', 'hanadi', 
    'hanan', 'huda', 'iman', 'karima', 'laila', 'latifa', 'leila', 'lina', 'malak', 'maryam', 'maya', 
    'mona', 'nadia', 'najwa', 'noor', 'noura', 'raja', 'raneem', 'rania', 'rasha', 'rawya', 'reem', 
    'safia', 'salma', 'samira', 'soraya', 'yasmin', 'zainab', 'zahra',
    
    // Indian names
    'aanya', 'aditi', 'aishwarya', 'alisha', 'amrita', 'ananya', 'anjali', 'anvi', 'aparna', 'deepa', 
    'deepika', 'divya', 'gauri', 'indira', 'jaya', 'karishma', 'kavya', 'kiara', 'kiran', 'lakshmi', 
    'meera', 'megha', 'nandini', 'neha', 'nisha', 'padma', 'pooja', 'priya', 'riya', 'saanvi', 'sarika', 
    'shreya', 'simran', 'siya', 'sneha', 'sunita', 'tanvi', 'trisha', 'uma', 'vaani', 'vani', 'varsha',
    
    // African names
    'adia', 'afua', 'aisha', 'akua', 'amara', 'amma', 'ayanna', 'babirye', 'chioma', 'dalia', 'ebele', 
    'eshe', 'faizah', 'folami', 'habiba', 'hasina', 'imani', 'kamaria', 'kesi', 'layla', 'makena', 
    'mandisa', 'malaika', 'nia', 'ola', 'oni', 'panya', 'rehema', 'safiya', 'sauda', 'sade', 'taja', 
    'talibah', 'thema', 'zahara', 'zola', 'zuri',
    
    // Professional titles that indicate female
    'mrs', 'ms', 'miss', 'madam', 'madame', 'lady'
  ]);
  
  // Get gender result
  const gender = femaleNames.has(firstName) ? 'female' : 'male';
  
  // Log the gender detection for debugging
  console.log(`[Avatar] Detected gender for "${firstName}": ${gender}`);
  
  return gender;
}

/**
 * Generates a random avatar URL based on gender
 */
function generateAvatarUrl(name: string, index: number): string {
  // Use name to determine gender for avatar
  const gender = determineGender(name);
  
  // Generate a truly random number between 0-99 for each persona
  // No need for consistency since personas are generated with random names each time
  const randomPortraitNumber = Math.floor(Math.random() * 100);
  
  // Return random portrait URL that matches gender
  return `https://randomuser.me/api/portraits/${gender === 'female' ? 'women' : 'men'}/${randomPortraitNumber}.jpg`;
}

/**
 * Generates personas for a LinkedIn post analysis.
 * This function can be used to preload personas in the background.
 */
export async function generatePersonas(formData: FormData): Promise<AudiencePersona[]> {
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in generatePersonas');
    throw new Error('Gemini API key is missing. Please set VITE_GOOGLE_API_KEY in your .env file.');
  }

  logger.info('Starting background persona generation');
  
  // Log exactly what data we have available at this point
  logger.info('Available data for persona generation:', {
    userRole: formData.userRole || 'Not specified',
    userIndustry: formData.userIndustry || 'Not specified',
    purpose: formData.purpose || 'Not specified',
    tone: formData.tone || 'Not specified',
    targetAudience: formData.targetAudience || 'Not specified'
  });
  
  // Generate 3 personas using Gemini API - updated with only the data we have at this point
  const personasPrompt = `
    Create 3 realistic LinkedIn professional personas who would be in the target audience: "${formData.targetAudience}".
    
    Consider the following user information:
    - User's Job/Role: ${formData.userRole || 'Not specified'} 
    - User's Industry: ${formData.userIndustry || 'Not specified'}
    - Post Purpose: ${formData.purpose || 'Not specified'}
    - Post Tone: ${formData.tone || 'Not specified'}
    
    Create personas that are DIRECTLY relevant to the author's professional background:
    1. At least one persona should be from the SAME industry as the author (${formData.userIndustry || 'their industry'})
    2. Include personas with varying levels of seniority/experience relative to the author
    3. Include at least one persona who would be a valuable connection for the author based on their role: ${formData.userRole || 'their role'}
    
    The personas should also represent professionals who would be most relevant to:
    1. The stated purpose: ${formData.purpose || 'Not specified'}
    2. The intended tone: ${formData.tone || 'Not specified'}
    3. The target audience: ${formData.targetAudience || 'Not specified'}
    
    IMPORTANT: Ensure each persona has a full name. Do not reuse first or last names across any personas.
    Try to have diversity in the personas' genders, with a mix of male and female names.
    
    For each persona, provide:
    1. Full name (unique from the other personas - no duplicate names)
    2. Job title
    3. Industry
    4. 3 professional interests
    5. A brief, unique personality trait
    
    Format the output as a JSON array where each object has:
    {
      "name": "Full Name",
      "title": "Job Title",
      "industry": "Industry",
      "interests": ["Interest 1", "Interest 2", "Interest 3"],
      "personality": "Brief personality description"
    }
    
    IMPORTANT: Ensure the JSON is correctly formatted with proper commas between properties and no trailing commas. Enclose all property names and string values in double quotes.
  `;

  let audiencePersonas: AudiencePersona[] = [];
  const personasResponse = await callGeminiAPI(personasPrompt, 'PERSONA_GENERATION_PRELOAD');
  if (personasResponse) {
    try {
      logger.info('Processing background persona generation response');
      
      // More robust JSON extraction with pre-processing
      let jsonStr = personasResponse;
      
      // Try to extract just the JSON array if the response contains other text
      const arrayMatch = jsonStr.match(/\[\s*\{[\s\S]*\}\s*\]/);
      if (arrayMatch) {
        jsonStr = arrayMatch[0];
      }
      
      // Log the extracted JSON for debugging
      logger.debug('Extracted JSON string:', jsonStr.substring(0, 100) + '...');
      
      // Clean up common JSON formatting issues
      const cleanedJson = jsonStr
        // Replace multiple newlines with a single space
        .replace(/\n+/g, ' ')
        // Fix trailing commas in objects (before closing brace)
        .replace(/,\s*}/g, '}')
        // Fix trailing commas in arrays (before closing bracket)
        .replace(/,\s*]/g, ']')
        // Fix missing commas between objects in array
        .replace(/}\s*{/g, '},{')
        // Fix missing quotes around property names
        .replace(/([{,]\s*)([a-zA-Z0-9_]+)(\s*:)/g, '$1"$2"$3')
        // Fix missing quotes around string values (this is simplified and may not catch all cases)
        .replace(/:\s*([a-zA-Z0-9_\s]+)(\s*[,}])/g, ': "$1"$2');
      
      // Log the cleaned JSON
      logger.debug('Cleaned JSON:', cleanedJson.substring(0, 100) + '...');
      
      try {
        // Try to parse the cleaned JSON
        const parsedPersonas = JSON.parse(cleanedJson);
        
        // Ensure we got a valid array
        if (!Array.isArray(parsedPersonas)) {
          logger.error('Parsed JSON is not an array:', typeof parsedPersonas);
          throw new Error('Parsed JSON is not an array');
        }
        
        // Ensure we only take exactly 3 personas regardless of how many are returned
        audiencePersonas = parsedPersonas.slice(0, 3).map((persona: any, index: number) => {
          // Ensure all required fields exist with fallbacks
          return {
            id: `${index + 1}`,
            name: persona.name || `Professional ${index + 1}`,
            title: persona.title || `Professional in ${formData.userIndustry || 'Industry'}`,
            industry: persona.industry || formData.userIndustry || 'Technology',
            interests: Array.isArray(persona.interests) ? persona.interests : ['Professional Development', 'Industry Trends', 'Networking'],
            avatar: generateAvatarUrl(persona.name || `Professional ${index + 1}`, index),
            personality: persona.personality || 'Thoughtful professional who values clear communication'
          };
        });
        
        // Apply uniqueness check
        audiencePersonas = ensureUniquePersonaNames(audiencePersonas);
        
        logger.info(`Successfully preloaded ${audiencePersonas.length} personas in the background`);
        return audiencePersonas;
      } catch (parseError) {
        logger.error('Error parsing cleaned JSON:', parseError);
        
        // As a last resort, try to manually create the personas from a simpler regex extraction
        try {
          logger.info('Attempting direct regex extraction of persona data');
          
          // Extract names
          const names = personasResponse.match(/name["\s:]+([^"]+)/gi)?.map((m: string) => m.replace(/name["\s:]+/i, '').trim()) || [];
          // Extract titles
          const titles = personasResponse.match(/title["\s:]+([^"]+)/gi)?.map((m: string) => m.replace(/title["\s:]+/i, '').trim()) || [];
          // Extract industries
          const industries = personasResponse.match(/industry["\s:]+([^"]+)/gi)?.map((m: string) => m.replace(/industry["\s:]+/i, '').trim()) || [];
          
          // If we found at least some data, construct personas manually
          if (names.length > 0) {
            logger.info(`Extracted ${names.length} persona names through direct regex`);
            audiencePersonas = names.slice(0, 3).map((name: string, index: number) => ({
              id: `${index + 1}`,
              name: name || `Professional ${index + 1}`,
              title: (titles[index] || `Professional in ${formData.userIndustry || 'Industry'}`),
              industry: (industries[index] || formData.userIndustry || 'Technology'),
              interests: ['Professional Development', 'Industry Trends', 'Networking'],
              avatar: generateAvatarUrl(name, index),
              personality: 'Thoughtful professional who values clear communication'
            }));
            
            // Apply uniqueness check
            audiencePersonas = ensureUniquePersonaNames(audiencePersonas);
            
            logger.info(`Successfully created ${audiencePersonas.length} personas through regex fallback`);
            return audiencePersonas;
          }
          
          throw new Error('Could not extract persona data through regex either');
        } catch (regexError) {
          logger.error('Even regex extraction failed:', regexError);
          throw new Error('Failed to parse persona data from Gemini API response');
        }
      }
    } catch (e) {
      logger.error('Error parsing Gemini personas response during preload:', e);
      throw new Error('Error parsing Gemini personas response. The AI model returned an invalid format.');
    }
  } else {
    throw new Error('Failed to generate personas from Gemini API. Please check your API key and network connection.');
  }
}

// Add this function to handle duplicate names after the personas are created
function ensureUniquePersonaNames(personas: AudiencePersona[]): AudiencePersona[] {
  const nameMap = new Map<string, number>();
  
  return personas.map((persona, index) => {
    // Track first names to detect duplicates
    const firstName = persona.name.split(' ')[0];
    const count = nameMap.get(firstName) || 0;
    nameMap.set(firstName, count + 1);
    
    // If this is a duplicate name, add a suffix or pick an alternative
    if (count > 0) {
      // Array of alternative names to use if duplicates occur
      const alternativeFirstNames = [
        'Michael', 'James', 'Robert', 'John', 'William', 'David', 'Thomas', 'Richard', 'Joseph',
        'Emily', 'Emma', 'Madison', 'Olivia', 'Hannah', 'Abigail', 'Isabella', 'Samantha', 'Elizabeth'
      ];
      
      // Create a new name using an alternative first name
      const lastName = persona.name.split(' ').slice(1).join(' ');
      const newFirstName = alternativeFirstNames[index % alternativeFirstNames.length];
      const newName = `${newFirstName} ${lastName}`;
      
      logger.info(`Found duplicate name: ${persona.name}, renamed to: ${newName}`);
      
      // Return updated persona with new name
      return {
        ...persona,
        name: newName,
        // Update avatar with new name to ensure it matches
        avatar: generateAvatarUrl(newName, index)
      };
    }
    
    return persona;
  });
}

/**
 * Processes any attachments once and extracts their content as text.
 * This avoids sending the same video/image multiple times to the API.
 */
export async function processAttachmentsOnce(formData: FormData): Promise<string> {
  let mediaContentDescription = '';
  
  // Process attachments if they exist
  if (formData.attachments?.length) {
    logger.info(`Processing ${formData.attachments.length} attachments once for reuse`);
    
    for (const attachment of formData.attachments) {
      // Handle poll type attachment
      if (attachment.type === 'poll') {
        logger.info(`Processing poll attachment with ${attachment.pollOptions?.length || 0} options`);
        
        const pollOptionsText = attachment.pollOptions?.map(option => `"${option.text}"`).join(', ') || '';
        const pollDuration = attachment.pollDuration || '1 week';
        
        mediaContentDescription += `\n\nPOLL CONTENT:\nThis is a LinkedIn poll with the following options: ${pollOptionsText}.\nThe poll duration is set to ${pollDuration}.\n\n`;
        logger.info(`Successfully processed poll content`);
        continue;
      }
      
      const fileSizeInMB = attachment.file?.size ? attachment.file.size / (1024 * 1024) : 0;
      logger.info(`Processing attachment: ${attachment.type}, file: ${attachment.file?.name} (${fileSizeInMB.toFixed(2)}MB)`);
      
      try {
        // For video and image attachments, we'll extract content once
        if ((attachment.type === 'video' || attachment.type === 'image') && attachment.file) {
          const base64Result = await fileToBase64(attachment.file);
          if (!base64Result) {
            logger.error(`Failed to encode ${attachment.type} for preprocessing: ${attachment.file.name}`);
            continue;
          }
          
          logger.info(`Successfully encoded ${attachment.type} file (${fileSizeInMB.toFixed(2)}MB) for preprocessing`);
          logger.info(`Using model gemini-2.5-flash-preview-04-17 for ${attachment.type} content extraction`);
          
          // Get content description from Gemini directly
          const contentDescription = await extractContentDescription(
            attachment.type, 
            base64Result.data,
            base64Result.mimeType,
            attachment.file.name
          );
          
          mediaContentDescription += `\n\n${attachment.type.toUpperCase()} CONTENT DESCRIPTION:\n${contentDescription}\n\n`;
          logger.info(`Successfully extracted ${attachment.type} content description (${contentDescription.length} chars)`);
          console.log(`[Media Processing] Extracted ${attachment.type} content: ${contentDescription.substring(0, 100)}...`);
        }
      } catch (error) {
        logger.error(`Error processing ${attachment.type} attachment for reuse: ${error}`);
        mediaContentDescription += `\n\nNote: There was an error processing the ${attachment.type} attachment. Please provide feedback based only on the text content.\n\n`;
      }
    }
  }
  
  return mediaContentDescription;
}

/**
 * Uses Gemini API to extract content description from media files
 */
async function extractContentDescription(
  mediaType: string,
  base64Data: string,
  mimeType: string,
  fileName: string
): Promise<string> {
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in extractContentDescription');
    throw new Error('Gemini API key is missing. Please set VITE_GOOGLE_API_KEY in your .env file.');
  }

  // Use our preferred model for content extraction
  const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
  const model = genAI.getGenerativeModel({
    model: "gemini-2.5-flash-preview-04-17",
  });

  let prompt = '';
  if (mediaType === 'image') {
    prompt = "Describe this image in detail. Include objects, people, colors, text, and other notable features.";
  } else if (mediaType === 'video') {
    prompt = "Describe this video in detail. Include visual content, any text shown, people, actions, and audio content including speech. Provide a comprehensive description that could be used as a complete substitute for someone who cannot view the video.";
  }

  try {
    logger.info(`Extracting content from ${mediaType}: ${fileName}`);
    
    const result = await model.generateContent([
      prompt,
      {
        inlineData: {
          mimeType: mimeType,
          data: base64Data
        }
      }
    ]);
    
    const description = result.response.text();
    return description || `Could not extract content from ${mediaType}.`;
  } catch (error) {
    logger.error(`Error extracting content from ${mediaType}: ${error}`);
    return `Error extracting content from ${mediaType}: ${error}`;
  }
}

// Extend the FormData interface to include our custom property
interface ExtendedFormData extends FormData {
  _processedMediaContent?: string;
}

/**
 * Generates analysis data for a LinkedIn post without improvements.
 * Can use preloaded personas if available.
 * Improvements are generated separately to avoid overloading the API response parsing.
 */
export async function generateAnalysis(formData: FormData, preloadedPersonas?: Promise<AudiencePersona[]> | null): Promise<AnalysisData> {
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in generateAnalysis');
    throw new Error('Gemini API key is missing. Please set VITE_GOOGLE_API_KEY in your .env file.');
  }

  let audiencePersonas: AudiencePersona[] = [];
  
  // Try to use preloaded personas if available
  if (preloadedPersonas) {
    try {
      logger.info('Using preloaded personas');
      audiencePersonas = await preloadedPersonas;
      logger.info(`Successfully retrieved ${audiencePersonas.length} preloaded personas`);
    } catch (error) {
      logger.error('Error using preloaded personas:', error);
      // We'll generate new personas below if this fails
    }
  }

  // If we don't have preloaded personas, generate them
  if (audiencePersonas.length === 0) {
    try {
      logger.info('Generating new personas');
      audiencePersonas = await generatePersonas(formData);
      logger.info(`Successfully generated ${audiencePersonas.length} new personas`);
    } catch (error) {
      logger.error('Error generating personas:', error);
      throw new Error(`Failed to generate audience personas: ${error}`);
    }
  }

  // Process any media attachments once to avoid sending them multiple times
  // Only process if they haven't already been processed during upload
  const extendedFormData = formData as ExtendedFormData;
  if (formData.attachments?.length && !extendedFormData._processedMediaContent) {
    try {
      logger.info('No pre-processed content found, processing attachments now');
      const processedContent = await processAttachmentsOnce(formData);
      // Store the processed content in the formData object for reuse
      extendedFormData._processedMediaContent = processedContent;
      logger.info('Successfully processed attachments once for all personas');
    } catch (error) {
      logger.error('Error processing attachments:', error);
      // Continue even if attachment processing fails
    }
  } else if (extendedFormData._processedMediaContent) {
    logger.info('Using pre-processed media content from earlier upload');
  }

  // Generate feedback for each persona in parallel
  logger.info('Starting feedback generation for each persona in parallel');
  const feedbackPromises = audiencePersonas.map(persona => 
    generatePersonaFeedback(persona, formData)
      .catch(error => {
        logger.error(`Error generating feedback for persona ${persona.id}:`, error);
        return null; // Return null for failed feedback
      })
  );

  // Wait for all feedback to be generated
  const feedbackResults = await Promise.all(feedbackPromises);
  const feedbackItems = feedbackResults.filter(item => item !== null) as FeedbackItem[];
  
  logger.info(`Successfully generated feedback for all ${feedbackItems.length} personas in parallel`);

  // Prepare the analysis data without improvements
  logger.info('Analysis complete, returning results without improvements');
  
  // Calculate scores (keeping this logic from the original implementation)
  const overallScore = Math.min(
    100, 
    Math.round(
      feedbackItems.reduce((sum, item) => sum + item.score, 0) / Math.max(1, feedbackItems.length)
    )
  );

  // Calculate engagement probability
  const engagementProbability = Math.min(
    100, 
    Math.round(overallScore * (0.7 + Math.random() * 0.3))
  );

  const analysisData: AnalysisData = {
    overallScore,
    engagementProbability,
    audiencePersonas,
    feedback: feedbackItems,
    improvements: [] // Improvements are generated separately
  };

  logger.info(`Analysis complete with ${audiencePersonas.length} personas and ${feedbackItems.length} feedback items`);
  return analysisData;
}

/**
 * Generates feedback from a persona for a LinkedIn post.
 */
async function generatePersonaFeedback(
  persona: AudiencePersona,
  formData: FormData
): Promise<FeedbackItem> {
  logger.info(`Generating feedback for persona: ${persona.name} (${persona.id})`);
  
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in generatePersonaFeedback');
    throw new Error('Gemini API key is missing');
  }

  // Check if we have media content
  let mediaPromptAddition = '';
  let hasMediaAttachments = false;
  
  // Check if we have attachments
  if (formData.attachments?.length) {
    hasMediaAttachments = true;
    console.log(`[Analysis] Found ${formData.attachments.length} attachments for persona: ${persona.name}`);
    
    // Use pre-processed media content if available
    const extendedFormData = formData as ExtendedFormData;
    if (extendedFormData._processedMediaContent) {
      // Don't include the full content description in the feedback itself,
      // but include it in the prompt to Gemini so it can generate better feedback
      mediaPromptAddition = extendedFormData._processedMediaContent;
      logger.info(`Using pre-processed media content for persona: ${persona.name}`);
    }
  }

  // Step 1: Generate the feedback comment first
  const feedbackPrompt = `
    You are ${persona.name}, a ${persona.title} in the ${persona.industry} industry with interests in ${persona.interests.join(', ')}.
    ${persona.personality ? `Your personality is: ${persona.personality}` : ''}
    
    Review this LinkedIn post and provide authentic, realistic feedback as yourself:
    
    POST PURPOSE: ${formData.purpose}
    POST TONE: ${formData.tone}
    POST CONTENT: ${formData.postContent}
    ${formData.attachments?.length ? `POST ATTACHMENTS: ${formData.attachments.map((a: Attachment) => a.type).join(', ')}` : 'POST ATTACHMENTS: None'}
    
    POST AUTHOR INFORMATION:
    - Industry: ${formData.userIndustry || 'Not specified'}
    - Role: ${formData.userRole || 'Not specified'}
    - Experience Level: ${formData.userExperience || 'Not specified'}
    
    Consider your relationship to the author:
    ${persona.industry === formData.userIndustry ? 
      '- You are in the SAME industry as the author, so consider how this post would be viewed by industry peers' : 
      '- You are in a DIFFERENT industry than the author, so consider if this content is accessible to those outside their field'}
    
    Your feedback should be specific to the post content and relate to both your professional background and the author's context.
    
    Include:
    1. Your honest assessment of the post (1-2 sentences)
    2. Whether you would engage with it (like, comment, share) and why
    3. One suggestion for improvement that considers the author's industry and role${formData.attachments?.length ? ' and the attached media' : ''}
    
    ${formData.attachments?.length ? `IMPORTANT: You must reference the ${formData.attachments.map(a => a.type).join('/')} in your feedback. Do not paste the full content description or be too verbose. Instead, specifically comment on 1-2 key elements of the ${formData.attachments.map(a => a.type).join('/')} that are most relevant to the post content.` : ''}
    
    Format your response as a JSON object:
    {
      "comment": "Your feedback here (DO NOT include the full media description, but DO reference key elements of it naturally in your feedback)",
      "likes": number (1-20),
      "dislikes": number (0-5)
    }
    
    ${mediaPromptAddition}
  `;
  
  // Call Gemini API with the prompt
  const feedbackResponse = await callGeminiAPI(feedbackPrompt, `FEEDBACK_GENERATION_${persona.id}`);
  
  logger.info(`Processing feedback response for persona: ${persona.name}`);
  const jsonStr = feedbackResponse.match(/\{[\s\S]*\}/)?.[0];
  
  if (!jsonStr) {
    logger.error(`Could not extract JSON object from Gemini feedback response for persona: ${persona.name}`);
    throw new Error(`Failed to generate proper feedback for ${persona.name}. API response didn't contain valid JSON.`);
  }
  
  const parsedFeedback = JSON.parse(jsonStr);
  logger.debug(`Successfully parsed feedback JSON for persona: ${persona.name}`);
  
  // Extract the feedback comment
  const feedbackComment = parsedFeedback.comment || 'No comment provided';
  const likes = parsedFeedback.likes || 5;
  const dislikes = parsedFeedback.dislikes || 0;
  
  // Step 2: Now ask for a score based on the generated comment
  const scorePrompt = `
    You are a LinkedIn engagement expert tasked with assigning a numerical score to feedback given by ${persona.name}, a ${persona.title} in the ${persona.industry} industry.
    
    This is the LinkedIn post:
    "${formData.postContent}"
    
    ${persona.name}'s feedback was:
    "${feedbackComment}"
    
    Based SOLELY on this feedback (which expresses ${persona.name}'s honest reaction to the post), 
    what score between 1 and 100 would accurately reflect their level of engagement and interest?
    
    Consider:
    - If the feedback is entirely negative or mentions the post is incomprehensible/irrelevant, the score should be very low (1-20)
    - If the feedback has mixed sentiments but is mostly negative, score should be below average (21-40)
    - If the feedback is neutral or has balanced positive and negative elements, score should be moderate (41-60)
    - If the feedback is generally positive with minor suggestions, score should be good (61-80)
    - If the feedback is enthusiastically positive, score should be excellent (81-100)
    
    The likes (${likes}) and dislikes (${dislikes}) can also influence your assessment.
    
    Respond ONLY with a number between 1 and 100.
  `;
  
  // Call Gemini API to get the score
  logger.info(`Generating score based on feedback for persona: ${persona.name}`);
  const scoreResponse = await callGeminiAPI(scorePrompt, `SCORE_GENERATION_${persona.id}`);
  
  // Extract the numeric score
  const scoreMatch = scoreResponse.match(/\d+/);
  let score = 50; // Default to a middle score if extraction fails
  
  if (scoreMatch) {
    score = Math.min(100, Math.max(1, parseInt(scoreMatch[0], 10)));
    logger.debug(`Successfully extracted score ${score} for persona: ${persona.name}`);
  } else {
    logger.warn(`Could not extract numeric score from response for persona: ${persona.name}, using default score of 50`);
  }
  
  return {
    id: `feedback-${persona.id}`,
    persona,
    score,
    comment: feedbackComment,
    likes,
    dislikes
  };
}

/**
 * Generates a chat response from a persona based on user message and history.
 */
export async function generatePersonaChatResponse(
  persona: AudiencePersona,
  userMessage: string,
  chatHistory: Array<{ text: string; sender: 'user' | 'persona' }>,
  formData: FormData
): Promise<string> {
  logger.info(`Generating chat response for persona: ${persona.name} (${persona.id})`);
  
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in generatePersonaChatResponse');
    throw new Error('Gemini API key is missing. Please set VITE_GOOGLE_API_KEY in your .env file.');
  }
  
  // Format chat history
  const formattedHistory = chatHistory.map(message => 
    `${message.sender === 'user' ? 'User' : persona.name}: ${message.text}`
  ).join('\n\n');
  
  // Check if we have media content
  let mediaPromptAddition = '';
  let hasMediaAttachments = false;
  
  // Check if we have attachments
  if (formData.attachments?.length) {
    hasMediaAttachments = true;
    console.log(`[Chat] Found ${formData.attachments.length} attachments for persona: ${persona.name}`);
    
    // Use pre-processed media content if available
    const extendedFormData = formData as ExtendedFormData;
    if (extendedFormData._processedMediaContent) {
      mediaPromptAddition = extendedFormData._processedMediaContent;
      logger.info(`Using pre-processed media content for chat with persona: ${persona.name}`);
    }
  }

  // Add the rest of the original function's implementation
  console.log(`[Chat] Preparing chat prompt for persona: ${persona.name} ${hasMediaAttachments ? 'with media' : 'without media'}`);
   
  const prompt = `
    You are ${persona.name}, a ${persona.title} in the ${persona.industry} industry with interests in ${persona.interests.join(', ')}.
    ${persona.personality ? `Your personality is: ${persona.personality}` : ''}
    
    You read the following LinkedIn post written by a ${formData.userRole || 'professional'} in the ${formData.userIndustry || 'tech'} industry:
    
    POST PURPOSE: ${formData.purpose}
    POST TONE: ${formData.tone}
    POST CONTENT: ${formData.postContent}
    ${formData.attachments?.length ? `POST ATTACHMENTS: ${formData.attachments.map((a: Attachment) => a.type).join(', ')}` : 'POST ATTACHMENTS: None'}
    
    POST AUTHOR INFORMATION:
    - Industry: ${formData.userIndustry || 'Not specified'}
    - Role: ${formData.userRole || 'Not specified'}
    - Experience Level: ${formData.userExperience || 'Not specified'}
    
    You are now chatting with the author of the post.
    When responding, consider both your own perspective as a ${persona.title} in ${persona.industry} 
    and how this relates to the author's industry (${formData.userIndustry || 'Not specified'}).
    
    Previous conversation:
    ${formattedHistory}
    
    User's latest message: "${userMessage}"
    
    Respond authentically as ${persona.name}, keeping in mind your professional background, interests, and personality.
    Your response should be helpful, specific, and reflect your professional perspective.
    Consider the author's industry, role, and experience when responding to their question.
    ${formData.attachments?.length ? `When discussing the post's effectiveness, refer specifically to both the text content AND the ${formData.attachments.map((a: Attachment) => a.type).join('/')} that goes with the post.` : ''}
    If you and the author are in similar industries, mention that connection.
    Don't use phrases like "As a [title]" or "In my role" too often. Just be natural as if this is a real conversation.
    Keep your response under 100 words.
    
    ${mediaPromptAddition}
  `;
  
  return await callGeminiAPI(prompt, `PERSONA_CHAT_${persona.id}`);
}

/**
 * Generates improvement suggestions for a LinkedIn post based on feedback.
 * Separated from the main analysis to make the process more stable.
 */
export async function generateImprovements(
  formData: FormData,
  feedback: FeedbackItem[]
): Promise<Improvement[]> {
  logger.info('Starting improvements generation process');
  
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in generateImprovements');
    throw new Error('Gemini API key is missing. Please set VITE_GOOGLE_API_KEY in your .env file.');
  }

  // Check for attachments to include inline
  let mediaPromptAddition = '';
  let hasMediaAttachments = false;
  
  // Check if we have pre-processed content
  const extendedFormData = formData as ExtendedFormData;
  if (extendedFormData._processedMediaContent) {
    mediaPromptAddition = extendedFormData._processedMediaContent;
    hasMediaAttachments = true;
    logger.info(`Using pre-processed media content for improvements generation`);
  } else if (formData.attachments?.length) {
    // Only needed for backwards compatibility - attachments should already be processed
    hasMediaAttachments = true;
    logger.info(`Found ${formData.attachments.length} attachments without pre-processed content`);
  }
  
  console.log(`[Improvements] Preparing improvements prompt ${hasMediaAttachments ? 'with media' : 'without media'}`);
  
  // Generate improvement suggestions based on the post and feedback
  const improvementsPrompt = `
    Review this LinkedIn post and the feedback it received:
    
    POST PURPOSE: ${formData.purpose}
    POST TONE: ${formData.tone}
    TARGET AUDIENCE: ${formData.targetAudience}
    POST CONTENT: ${formData.postContent}
    ${formData.attachments?.length ? `POST ATTACHMENTS: ${formData.attachments.map((a: Attachment) => a.type).join(', ')}` : 'POST ATTACHMENTS: None'}
    
    AUTHOR INFORMATION:
    - Industry: ${formData.userIndustry || 'Not specified'}
    - Role: ${formData.userRole || 'Not specified'}
    - Experience Level: ${formData.userExperience || 'Not specified'}
    
    FEEDBACK SUMMARY:
    ${feedback.map(item => `- ${item.persona.name} (${item.persona.title}): ${item.comment}`).join('\n')}
    
    Based on this feedback and LinkedIn engagement best practices, generate 5 specific, actionable improvements for this LinkedIn post.
    Make these improvements industry-specific and tailored to the author's role as a ${formData.userRole || 'professional'} in ${formData.userIndustry || 'their industry'}.
    
    When crafting improvements, consider these LinkedIn post success factors:
    Remember that LinkedIn truncates content after 2-3 lines, so consider if the post needs a stronger hook in the first line. Optimal content length for engagement is typically 1,500-2,000 characters, with strategic whitespace for mobile readability (short paragraphs of 1-3 sentences). Effective posts often include a call-to-action that encourages commenting, personal storytelling elements for authenticity, specific numbers or data points for credibility, and inclusive language addressing the audience directly. Consider whether industry-specific hashtags (3-5 maximum, placed at the end) would enhance discoverability and if the post could benefit from conversation starters like questions to boost comment engagement. For visual content, evaluate if the post effectively uses images with text overlays, carousel posts, or native video.
    
    For each improvement, consider how it aligns with both the feedback and these factors:
    - Industry best practices and terminology for ${formData.userIndustry || 'their industry'}
    - What would resonate with others in the same role: ${formData.userRole || 'the author\'s role'}
    - The author's experience level: ${formData.userExperience || 'Not specified'}
    - LinkedIn's algorithm preferences (content that generates dwell time and quality comments)
    ${formData.attachments?.length ? 
      '- How the attached media could be better leveraged, optimized, or supplemented with text to create a stronger overall message' : 
      '- Whether adding relevant visual content would enhance engagement'}
    
    Categorize each improvement as one of: "content", "format", "engagement", "audience"${formData.attachments?.length ? ', or "media"' : ''}.
    
    ${formData.attachments?.length ? 
      'Include at least one "media" improvement that addresses how the media and text could work together more effectively.' : 
      ''}
    
    For each improvement, provide:
    1. A specific suggestion (one sentence)
    2. The impact level ("high", "medium", or "low")
    
    IMPORTANT: Format your response EXACTLY as a valid JSON array:
    [
      {
        "type": "content",
        "suggestion": "First improvement suggestion",
        "impact": "high"
      },
      {
        "type": "format",
        "suggestion": "Second improvement suggestion",
        "impact": "medium"
      }
    ]
    
    Make sure to use proper JSON formatting with double quotes around property names and string values.
    
    ${mediaPromptAddition}
  `;
  
  const improvementsResponse = await callGeminiAPI(improvementsPrompt, 'IMPROVEMENTS_GENERATION');
  
  logger.info('Processing improvements generation response');
  const jsonStr = improvementsResponse.match(/\[[\s\S]*\]/)?.[0];
  if (!jsonStr) {
    logger.error('Could not extract JSON array from Gemini improvements response');
    throw new Error('Failed to generate improvements. API response didn\'t contain valid JSON.');
  }
  
  const parsedImprovements = JSON.parse(jsonStr);
  const improvements = parsedImprovements.map((imp: any, index: number) => ({
    id: `imp-${index + 1}`,
    type: imp.type || 'content',
    suggestion: imp.suggestion || `Improvement suggestion ${index + 1}`,
    impact: imp.impact || 'medium'
  }));
  
  logger.info(`Successfully generated ${improvements.length} improvements`);
  return improvements;
}

/**
 * Generates an improved LinkedIn post based on original content and improvement suggestions.
 */
export async function generateImprovedPost(
  formData: FormData,
  improvements: Improvement[]
): Promise<string> {
  logger.info('Starting improved post generation');
  
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in generateImprovedPost');
    throw new Error('Gemini API key is missing. Please set VITE_GOOGLE_API_KEY in your .env file.');
  }

  // Check for attachments to include inline
  let mediaPromptAddition = '';
  let hasMediaAttachments = false;
  
  // Check if we have pre-processed content
  const extendedFormData = formData as ExtendedFormData;
  if (extendedFormData._processedMediaContent) {
    mediaPromptAddition = extendedFormData._processedMediaContent;
    hasMediaAttachments = true;
    logger.info('Using pre-processed media content for improved post generation');
  } else if (formData.attachments?.length) {
    hasMediaAttachments = true;
    logger.info(`Found ${formData.attachments.length} attachments without pre-processed content`);
  }
  
  console.log(`[Improved Post] Preparing improved post prompt ${hasMediaAttachments ? 'with media' : 'without media'}`);
  
  // Format improvements by categories for better prompt clarity
  const contentImprovements = improvements
    .filter(imp => imp.type === 'content')
    .map(imp => `- ${imp.suggestion} (Impact: ${imp.impact})`)
    .join('\n');
    
  const formatImprovements = improvements
    .filter(imp => imp.type === 'format')
    .map(imp => `- ${imp.suggestion} (Impact: ${imp.impact})`)
    .join('\n');
  
  const engagementImprovements = improvements
    .filter(imp => imp.type === 'engagement')
    .map(imp => `- ${imp.suggestion} (Impact: ${imp.impact})`)
    .join('\n');
  
  const audienceImprovements = improvements
    .filter(imp => imp.type === 'audience')
    .map(imp => `- ${imp.suggestion} (Impact: ${imp.impact})`)
    .join('\n');
  
  const mediaImprovements = improvements
    .filter(imp => imp.type === 'media')
    .map(imp => `- ${imp.suggestion} (Impact: ${imp.impact})`)
    .join('\n');
  
  // Generate improved post based on the original and incorporating suggestions
  const improvedPostPrompt = `
    You're a LinkedIn post optimization expert. Your task is to rewrite a LinkedIn post 
    to make it more effective, engaging, and optimized for the audience.
    
    ORIGINAL POST:
    ${formData.postContent}
    
    POST DETAILS:
    - Purpose: ${formData.purpose}
    - Tone: ${formData.tone}
    - Target Audience: ${formData.targetAudience}
    ${formData.attachments?.length ? `- Attachments: ${formData.attachments.map((a: Attachment) => a.type).join(', ')}` : '- Attachments: None'}
    
    AUTHOR INFORMATION:
    - Industry: ${formData.userIndustry || 'Not specified'}
    - Role: ${formData.userRole || 'Not specified'}
    - Experience Level: ${formData.userExperience || 'Not specified'}
    
    IMPROVEMENT SUGGESTIONS TO INCORPORATE:
    ${contentImprovements ? `CONTENT IMPROVEMENTS:\n${contentImprovements}\n\n` : ''}
    ${formatImprovements ? `FORMAT IMPROVEMENTS:\n${formatImprovements}\n\n` : ''}
    ${engagementImprovements ? `ENGAGEMENT IMPROVEMENTS:\n${engagementImprovements}\n\n` : ''}
    ${audienceImprovements ? `AUDIENCE IMPROVEMENTS:\n${audienceImprovements}\n\n` : ''}
    ${mediaImprovements ? `MEDIA IMPROVEMENTS:\n${mediaImprovements}\n\n` : ''}
    
    INSTRUCTIONS:
    1. Rewrite the original post incorporating the improvement suggestions
    2. Maintain the original intent and key points
    3. Optimize for engagement and readability
    4. Keep the post professional and appropriate for LinkedIn
    5. Use formatting appropriately (line breaks, emojis, bullet points) if suggested
    6. If there are media improvements, incorporate text that enhances and refers to the attached media
    7. Include appropriate hashtags if suggested
    8. Keep the response CONCISE, at approximately the same length as the original unless length was specifically mentioned as an issue
    
    IMPORTANT: Only respond with the improved post text. Do not include any explanation or comments.
    
    ${mediaPromptAddition}
  `;
  
  return await callGeminiAPI(improvedPostPrompt, 'IMPROVED_POST_GENERATION');
}

/**
 * Generates the best time to post for a LinkedIn post based on industry, purpose, and audience.
 */
export async function generateBestTimeToPost(formData: FormData): Promise<string> {
  logger.info('Starting best time to post generation');
  
  if (!GEMINI_API_KEY) {
    logger.error('Missing API key in generateBestTimeToPost');
    throw new Error('Gemini API key is missing. Please set VITE_GOOGLE_API_KEY in your .env file.');
  }

  const bestTimePrompt = `
    As a LinkedIn engagement expert, determine the optimal time to post for maximum engagement.
    
    POST DETAILS:
    - Purpose: ${formData.purpose}
    - Tone: ${formData.tone}
    - Target Audience: ${formData.targetAudience}
    - Industry: ${formData.userIndustry || 'Not specified'}
    
    Consider these factors:
    1. The industry of the poster (${formData.userIndustry || 'Not specified'})
    2. The target audience (${formData.targetAudience})
    3. The purpose of the post (${formData.purpose})
    4. Best practices for LinkedIn engagement timing
    
    Based on research and best practices, provide the optimal day(s) of the week and time(s) to post this content for maximum visibility and engagement.
    
    Format your response as a single string like: "Tuesday and Thursday between 9-10 AM or 1-2 PM" or "Weekdays between 8-10 AM in your audience's timezone"
    
    IMPORTANT: Be specific while being brief. Provide ONLY the recommended posting time without any explanation or additional content.
  `;
  
  try {
    const timeResponse = await callGeminiAPI(bestTimePrompt, 'BEST_TIME_GENERATION');
    // Clean up response to ensure we get just the time recommendation
    const cleanedResponse = timeResponse
      .replace(/^["']|["']$/g, '') // Remove quotes if present
      .replace(/^Best time to post:?\s*/i, '') // Remove any prefixes
      .replace(/^The best time to post:?\s*/i, '')
      .replace(/^Recommended time:?\s*/i, '')
      .trim();
    
    logger.info(`Successfully generated best time to post: ${cleanedResponse}`);
    return cleanedResponse;
  } catch (error) {
    logger.error('Error generating best time to post:', error);
    return 'Weekdays between 9-11 AM or 1-3 PM'; // Default fallback
  }
}
